import { useGetCurrentProjectId } from '@highlight-cp/hooks';
import { resolveScreenName, trackPageViewed } from '@highlight-cp/user-analytics';
import Router from 'next/router';
import { useEffect, useRef } from 'react';

import { useClientPortalAuth } from '../../../auth/ClientPortalAuthProvider';

/**
 * Owns customer-app navigation tracking in one place: emits a single curated
 * `Page Viewed` event on the initial (server-rendered) load and on every client-side
 * navigation, resolving the current `router.pathname` through the central
 * `ROUTE_SCREEN_MAP`.
 *
 * Mounted inside the provider tree (Redux `<Provider>` for the project id and
 * `<ClientPortalAuthProvider>` for the org id), so it never renders on the provider-less
 * error path where `useClientPortalAuth()` would throw. Renders nothing.
 */
export default function PageViewTracker() {
  const auth = useClientPortalAuth();
  const projectId = useGetCurrentProjectId();

  const org_id = auth.status === 'authenticated' ? String(auth.session.org_id) : undefined;
  const project_id = projectId != null ? projectId.toString() : undefined;

  // Keep the latest context available to `fire()` without making it an effect dependency.
  // `org_id`/`project_id` both resolve asynchronously after mount (auth settles after a
  // session fetch; the project id is set after an RTK Query resolves), so keying the effect
  // on them would re-run it — and re-fire — on every context change, double-counting each
  // page view. Reading them from a ref at fire time decouples "context resolved" from
  // "navigation happened", so only a real navigation emits an event.
  const contextRef = useRef({ org_id, project_id });
  contextRef.current = { org_id, project_id };

  // The initial server-rendered load has no `routeChangeComplete`, so we fire it manually —
  // but only once auth has settled (out of `loading`), so an authenticated deep-link load
  // still carries `org_id`. A subsequent `project_id` resolving is best-effort and may be
  // absent on a project route's first paint (by design).
  const authSettled = auth.status !== 'loading';
  const hasFiredInitialLoad = useRef(false);

  useEffect(() => {
    // Read the *current* pathname and context at fire time — `routeChangeComplete` runs
    // after the route has changed. Any real emit marks the initial load as done, so a
    // navigation that completes before auth settles can't be re-counted by the deferred
    // initial fire below.
    const fire = () => {
      const screen_name = resolveScreenName(Router.pathname);

      // Skip both redirect-only routes (null) and unmapped/error routes (undefined).
      if (!screen_name) {
        return;
      }

      hasFiredInitialLoad.current = true;
      trackPageViewed({ screen_name, ...contextRef.current });
    };

    // Defer the initial fire to the next macrotask and cancel it if a navigation starts
    // first. `AuthGuard` issues its `router.replace()` redirects (bouncing an authenticated
    // user off `/auth/login`, or an unauthenticated user off a protected route) on the *same*
    // auth-settled transition that triggers this effect, and `router.replace()` is async —
    // firing synchronously here would emit a `Page Viewed` for the pre-redirect screen the
    // user never actually lands on. Deferring lets the redirect's `routeChangeStart` cancel
    // the manual fire, so the following `routeChangeComplete` supplies the one correct event.
    let initialFireTimer: ReturnType<typeof setTimeout> | undefined;
    const cancelInitialFire = () => clearTimeout(initialFireTimer);

    if (authSettled && !hasFiredInitialLoad.current) {
      initialFireTimer = setTimeout(() => {
        if (!hasFiredInitialLoad.current) {
          fire();
        }
      });
    }

    Router.events.on('routeChangeStart', cancelInitialFire);
    Router.events.on('routeChangeComplete', fire);
    return () => {
      cancelInitialFire();
      Router.events.off('routeChangeStart', cancelInitialFire);
      Router.events.off('routeChangeComplete', fire);
    };
  }, [authSettled]);

  return null;
}
